import Fuse from 'fuse.js';
import type { Answer, Question } from 'src/redux/survey/survey.types';

/** Minimum confidence (0–1) to auto-apply suggestions; below this, leave unmapped. */
export const SUGGESTION_MIN_CONFIDENCE = 0.9;

export interface QuestionSuggestion {
  anchorQuestionId: string;
  memberQuestionId: string;
  /** Higher is better; derived as `1 - fuseScore` (Fuse: lower distance = better match). */
  score: number;
}

export interface AnswerSuggestion {
  anchorAnswerId: string;
  memberAnswerId: string;
  score: number;
}

const fuseQuestionKeys = ['title', 'type'] as const;

function normaliseSearchQuery(value: string | null | undefined): string {
  if (value == null || typeof value !== 'string') {
    return '';
  }
  return value;
}

/** Top-N fuzzy matches from member questions to a single anchor question title. */
export function suggestMemberQuestionForAnchor(
  anchor: Question,
  memberQuestions: Question[],
  limit = 5
): QuestionSuggestion[] {
  if (memberQuestions.length === 0) return [];
  const query = normaliseSearchQuery(anchor.title);
  if (!query.trim()) {
    return [];
  }
  const fuse = new Fuse(
    memberQuestions.map((q) => ({ ...q, title: normaliseSearchQuery(q.title) })),
    {
      keys: [...fuseQuestionKeys],
      threshold: 0.45,
      ignoreLocation: true,
    }
  );
  const results = fuse.search(query, { limit: limit + 5 });
  return results
    .map((r) => ({
      anchorQuestionId: anchor.id,
      memberQuestionId: r.item.id,
      score: 1 - (r.score ?? 0),
    }))
    .filter((s) => s.memberQuestionId !== anchor.id)
    .slice(0, limit);
}

/** Suggest member answers for one anchor answer (same question pair). */
export function suggestMemberAnswerForAnchor(
  anchorAnswer: Answer,
  memberAnswers: Answer[],
  limit = 5
): AnswerSuggestion[] {
  if (memberAnswers.length === 0) return [];
  const query = normaliseSearchQuery(anchorAnswer.title);
  if (!query.trim()) {
    return [];
  }
  const fuse = new Fuse(
    memberAnswers.map((a) => ({ ...a, title: normaliseSearchQuery(a.title) })),
    {
      keys: ['title'],
      threshold: 0.45,
      ignoreLocation: true,
    }
  );
  const results = fuse.search(query, { limit: limit + 5 });
  return results
    .map((r) => ({
      anchorAnswerId: anchorAnswer.id,
      memberAnswerId: r.item.id,
      score: 1 - (r.score ?? 0),
    }))
    .slice(0, limit);
}
